"use client";

import { parse, unparse } from "papaparse";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TEST_IDS } from "../../../constants/testIds";
import { useCanvas } from "../../../contexts/CanvasContext";
import { Skeleton } from "../../ui/skeleton";

const INITIAL_ROWS = 1000;
const INITIAL_COLS = 100; // Support up to column CV
const DEFAULT_COL_WIDTH = 120;
const ROW_HEIGHT = 32;
const HEADER_HEIGHT = 32;
const ROW_NUMBER_WIDTH = 50;

// Convert column index to Excel-style letters (A, B, C, ... Z, AA, AB, ...)
const getColumnLabel = (index) => {
  let result = "";
  let num = index;
  while (num >= 0) {
    result = String.fromCharCode(65 + (num % 26)) + result;
    num = Math.floor(num / 26) - 1;
    if (num < 0) break;
  }
  return result;
};

const PureSpreadsheetEditor = ({ content, onChange, readOnly = false }) => {
  const { updateContent } = useCanvas();
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  // Initialize data with larger grid
  const parseData = useMemo(() => {
    // Create empty grid
    const emptyGrid = Array(INITIAL_ROWS)
      .fill(null)
      .map(() => Array(INITIAL_COLS).fill(""));

    if (!content) return emptyGrid;

    try {
      const result = parse(content, { skipEmptyLines: false });

      // Fill the grid with parsed data
      result.data.forEach((row, rowIndex) => {
        if (rowIndex < INITIAL_ROWS) {
          row.forEach((cell, colIndex) => {
            if (colIndex < INITIAL_COLS) {
              emptyGrid[rowIndex][colIndex] = cell || "";
            }
          });
        }
      });

      return emptyGrid;
    } catch (error) {
      console.error("🎨 SpreadsheetEditor: Error parsing CSV content:", error);
      return emptyGrid;
    }
  }, [content]);

  const [localData, setLocalData] = useState(parseData);
  const [columnWidths, setColumnWidths] = useState(() =>
    Array(INITIAL_COLS).fill(DEFAULT_COL_WIDTH)
  );

  useEffect(() => {
    setLocalData(parseData);
  }, [parseData]);

  // Handle container resize
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setContainerSize({ width, height });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const generateCsv = useCallback((data) => {
    // Only export non-empty rows and columns
    const nonEmptyData = data
      .map((row) => {
        // Find last non-empty cell in row
        let lastIndex = -1;
        for (let i = row.length - 1; i >= 0; i--) {
          if (row[i] && row[i].toString().trim()) {
            lastIndex = i;
            break;
          }
        }
        return lastIndex >= 0 ? row.slice(0, lastIndex + 1) : [];
      })
      .filter((row) => row.length > 0);

    return unparse(nonEmptyData);
  }, []);

  const handleCellChange = useCallback(
    (rowIndex, colIndex, value) => {
      if (readOnly) return;

      const newData = [...localData];
      newData[rowIndex] = [...newData[rowIndex]];
      newData[rowIndex][colIndex] = value;

      setLocalData(newData);

      const csvContent = generateCsv(newData);

      // Update canvas context
      if (updateContent) {
        updateContent(csvContent, "user");
      }

      // Call external onChange if provided
      if (onChange) {
        onChange(csvContent);
      }
    },
    [readOnly, localData, generateCsv, updateContent, onChange]
  );

  const handleColumnResize = useCallback((colIndex, newWidth) => {
    setColumnWidths((prev) => {
      const newWidths = [...prev];
      newWidths[colIndex] = Math.max(80, newWidth);
      return newWidths;
    });
  }, []);

  const handleMouseDown = useCallback(
    (e, colIndex) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = columnWidths[colIndex];

      const handleMouseMove = (e) => {
        const deltaX = e.clientX - startX;
        const newWidth = startWidth + deltaX;
        handleColumnResize(colIndex, newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths, handleColumnResize]
  );

  // Calculate visible range
  const visibleStartRow = Math.floor(scrollTop / ROW_HEIGHT);
  const visibleEndRow = Math.min(
    INITIAL_ROWS,
    visibleStartRow + Math.ceil(containerSize.height / ROW_HEIGHT) + 1
  );

  const visibleStartCol = useMemo(() => {
    let currentX = 0;
    for (let i = 0; i < INITIAL_COLS; i++) {
      if (currentX + columnWidths[i] > scrollLeft) {
        return i;
      }
      currentX += columnWidths[i];
    }
    return 0;
  }, [scrollLeft, columnWidths]);

  const visibleEndCol = useMemo(() => {
    let currentX = 0;
    for (let i = 0; i < INITIAL_COLS; i++) {
      currentX += columnWidths[i];
      if (currentX > scrollLeft + containerSize.width) {
        return Math.min(INITIAL_COLS, i + 2);
      }
    }
    return INITIAL_COLS;
  }, [scrollLeft, containerSize.width, columnWidths]);

  // Calculate total dimensions
  const totalWidth =
    columnWidths.reduce((sum, width) => sum + width, 0) + ROW_NUMBER_WIDTH;
  const totalHeight = INITIAL_ROWS * ROW_HEIGHT + HEADER_HEIGHT;

  // Handle scroll
  const handleScroll = useCallback((e) => {
    setScrollLeft(e.target.scrollLeft);
    setScrollTop(e.target.scrollTop);
  }, []);

  // Check if we have meaningful content to display
  const hasContent = useMemo(() => {
    return localData.some(
      (row, rowIndex) =>
        rowIndex < 20 &&
        row.some(
          (cell, colIndex) =>
            colIndex < 10 && cell && cell.toString().trim().length > 0
        )
    );
  }, [localData]);

  // Show skeleton if no meaningful content
  if (!hasContent) {
    const isDark = document.documentElement.classList.contains("dark");

    return (
      <div
        className={`h-full w-full p-4 space-y-4 ${isDark ? "bg-[#2F2F2F]" : "bg-[#FAFAFA]"}`}
      >
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 24 }, (_, i) => (
            <Skeleton
              key={i}
              className={`h-8 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
            />
          ))}
        </div>
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 24 }, (_, i) => (
            <Skeleton
              key={i}
              className={`h-8 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
            />
          ))}
        </div>
        <div className="grid grid-cols-6 gap-2">
          {Array.from({ length: 24 }, (_, i) => (
            <Skeleton
              key={i}
              className={`h-8 ${isDark ? "bg-gray-600/30" : "bg-gray-300/30"}`}
            />
          ))}
        </div>
      </div>
    );
  }

  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div
      ref={containerRef}
      className={`h-full w-full relative ${isDark ? "bg-[#2F2F2F]" : "bg-[#FAFAFA]"}`}
      data-testid={TEST_IDS.CANVAS_SPREADSHEET_EDITOR}
    >
      {/* Fixed header row */}
      <div
        className={`absolute top-0 left-0 z-10 flex ${isDark ? "bg-[#2F2F2F]" : "bg-[#FAFAFA]"}`}
        style={{
          transform: `translateX(-${scrollLeft}px)`,
          width: totalWidth,
        }}
      >
        {/* Corner cell */}
        <div
          className={`flex items-center justify-center text-xs ${isDark ? "text-gray-400" : "text-gray-600"} font-medium border ${isDark ? "border-gray-600" : "border-gray-300"}`}
          style={{
            width: ROW_NUMBER_WIDTH,
            height: HEADER_HEIGHT,
            backgroundColor: isDark ? "#3a3a3a" : "#F0F0F0",
          }}
        >
          #
        </div>

        {/* All column headers (not just visible ones for smooth scrolling) */}
        {Array.from({ length: INITIAL_COLS }, (_, colIndex) => (
          <div
            key={colIndex}
            className={`relative flex items-center justify-center text-xs ${isDark ? "text-gray-400" : "text-gray-600"} font-medium border ${isDark ? "border-gray-600" : "border-gray-300"}`}
            style={{
              width: columnWidths[colIndex],
              height: HEADER_HEIGHT,
              backgroundColor: isDark ? "#3a3a3a" : "#F0F0F0",
            }}
          >
            {getColumnLabel(colIndex)}
            {/* Resize handle */}
            <div
              className={`absolute right-0 top-0 w-1 h-full cursor-col-resize ${isDark ? "hover:bg-blue-500" : "hover:bg-blue-600"} transition-colors`}
              onMouseDown={(e) => handleMouseDown(e, colIndex)}
            />
          </div>
        ))}
      </div>

      {/* Scrollable content area */}
      <div
        className="absolute left-0 right-0 bottom-0 overflow-auto"
        style={{ top: HEADER_HEIGHT }}
        onScroll={handleScroll}
      >
        {/* Virtual container for data rows */}
        <div
          style={{
            width: totalWidth,
            height: totalHeight - HEADER_HEIGHT,
            position: "relative",
          }}
        >
          {/* Data rows */}
          {Array.from({ length: visibleEndRow - visibleStartRow }, (_, i) => {
            const rowIndex = visibleStartRow + i;
            return (
              <div
                key={rowIndex}
                className="flex"
                style={{
                  position: "absolute",
                  top: rowIndex * ROW_HEIGHT,
                  left: 0,
                }}
              >
                {/* Row number */}
                <div
                  className={`flex items-center justify-center text-xs ${isDark ? "text-gray-400" : "text-gray-600"} font-medium border ${isDark ? "border-gray-600" : "border-gray-300"}`}
                  style={{
                    width: ROW_NUMBER_WIDTH,
                    height: ROW_HEIGHT,
                    backgroundColor: isDark ? "#3a3a3a" : "#F0F0F0",
                  }}
                >
                  {rowIndex + 1}
                </div>

                {/* Data cells */}
                {Array.from(
                  { length: visibleEndCol - visibleStartCol },
                  (_, j) => {
                    const colIndex = visibleStartCol + j;
                    return (
                      <input
                        key={`${rowIndex}-${colIndex}`}
                        type="text"
                        value={localData[rowIndex][colIndex] || ""}
                        onChange={(e) =>
                          handleCellChange(rowIndex, colIndex, e.target.value)
                        }
                        className={`bg-transparent border ${isDark ? "border-gray-600 text-white focus:ring-blue-500 focus:bg-gray-800/50" : "border-gray-300 text-gray-900 focus:ring-blue-600 focus:bg-gray-100/50"} text-xs px-1 focus:outline-none focus:ring-1`}
                        style={{
                          width: columnWidths[colIndex],
                          height: ROW_HEIGHT,
                        }}
                        disabled={readOnly}
                      />
                    );
                  }
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.content === nextProps.content &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.onChange === nextProps.onChange
  );
}

PureSpreadsheetEditor.propTypes = {
  content: PropTypes.string,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

const SpreadsheetEditor = memo(PureSpreadsheetEditor, areEqual);

export default SpreadsheetEditor;
